import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, BookOpen, ChefHat, ClipboardList, CreditCard, DoorOpen, FileSpreadsheet, LayoutDashboard, LogOut, Printer, QrCode, ScrollText, Settings, Users, Wallet, type LucideIcon } from 'lucide-react';
import { StaffReports } from './staff/StaffReports';
import { StaffExitDesk } from './staff/StaffExitDesk';
import { StaffAccounts } from './staff/StaffAccounts';
import { StaffPaymentsIntegration } from './staff/StaffPaymentsIntegration';
import { StationPicker, StationScreen } from './staff/StaffProduction';
import { StaffTeam } from './staff/StaffTeam';
import { StaffDashboard } from './staff/StaffDashboard';
import { StaffPrinting } from './staff/StaffPrinting';
import { useStaffRealtime } from '../lib/realtime';
import { StaffMenu } from './staff/StaffMenu';
import { StaffTables, TablePrint } from './staff/StaffTables';
import { StaffOrders } from './staff/StaffOrders';
import { StaffOrderEntry } from './staff/StaffOrderEntry';
import { StaffSettings } from './staff/StaffSettings';
import { PERMISSIONS, type Permission } from '@garlic/shared';
import { api, ApiError, apiUrl, post } from '../lib/api';
import { staffPath } from '../lib/paths';
import { AUDIT_LABEL, formatDateTime } from '../lib/format';
import { Alert, Badge, Button, Card, CardHeader, EmptyState, PageHeader, Spinner, cx } from '../ui/primitives';
import { RestaurantAvatar } from './RestaurantBadge';
import type { TenantRestaurant } from './TenantApp';
import { ProductionAlert, useProductionHealth } from './staff/ProductionAlert';

type Me = { user: { id: string; name: string; email: string }; role: { id: string; name: string; key: string | null }; permissions: Permission[] };

export function useStaffMe() {
  return useQuery({ queryKey: ['staff-me'], queryFn: () => api<Me>('/api/staff/me'), retry: false, staleTime: 60_000 });
}

export function StaffApp({ restaurant }: { restaurant: TenantRestaurant }) {
  const me = useStaffMe();
  const location = useLocation();
  if (me.isPending) return <Spinner />;
  if (me.isError) {
    if (me.error instanceof ApiError && (me.error.status === 401 || me.error.status === 403)) {
      return <Navigate to={staffPath('/login')} replace state={{ from: location.pathname }} />;
    }
    return <div className="p-6"><Alert tone="danger">Não foi possível carregar sua sessão.</Alert></div>;
  }
  return (
    <Routes>
      {me.data.permissions.includes('tables.view') && <Route path="mesas/imprimir" element={<TablePrint restaurant={restaurant} />} />}
      <Route path="*" element={<StaffShell restaurant={restaurant} me={me.data} />} />
    </Routes>
  );
}

/** `to` é relativo ao painel do restaurante (/painel/{slug}{to}). */
type NavItem = { to: string; label: string; icon: LucideIcon; perm?: Permission[]; phase?: number; end?: boolean };
const NAV: NavItem[] = [
  { to: '', label: 'Início', icon: LayoutDashboard, end: true },
  { to: '/producao', label: 'Produção', icon: ChefHat, perm: ['production.kitchen', 'production.bar'] },
  { to: '/pedidos', label: 'Pedidos', icon: ClipboardList, perm: ['orders.view'] },
  { to: '/mesas', label: 'Mesas e QR Codes', icon: QrCode, perm: ['tables.view'] },
  { to: '/cardapio', label: 'Cardápio', icon: BookOpen, perm: ['menu.view'] },
  { to: '/contas', label: 'Contas e caixa', icon: Wallet, perm: ['accounts.view'] },
  { to: '/saida', label: 'Saída (balcão)', icon: DoorOpen, perm: ['exit.validate'] },
  { to: '/pagamentos', label: 'Pagamentos online', icon: CreditCard, perm: ['payments.integration.manage'] },
  { to: '/equipe', label: 'Equipe', icon: Users, perm: ['staff.view'] },
  { to: '/impressao', label: 'Impressão', icon: Printer, perm: ['printing.manage'] },
  { to: '/relatorios', label: 'Relatórios', icon: BarChart3, perm: ['reports.financial.view', 'audit.view'] },
  { to: '/auditoria', label: 'Auditoria', icon: ScrollText, perm: ['audit.view'] },
  { to: '/configuracoes', label: 'Configurações', icon: Settings, perm: ['restaurant.settings.view'] },
];

function StaffShell({ restaurant, me }: { restaurant: TenantRestaurant; me: Me }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const live = useStaffRealtime();
  const can = (p?: Permission) => !p || me.permissions.includes(p);
  const health = useProductionHealth();
  // Telas de produção são opcionais: o menu só aparece se algum setor usa tela, ou se a impressora de um setor
  // só com impressora está com problema (aí a tela serve de reserva).
  const showProduction = !!health.data && (health.data.screens > 0 || health.data.issues.length > 0);
  const items = NAV.filter((n) => (!n.perm || n.perm.some((p) => me.permissions.includes(p))) && (n.to !== '/producao' || showProduction));
  const logout = async () => {
    await post('/api/staff/auth/logout').catch(() => {});
    qc.removeQueries({ queryKey: ['staff-me'] });
    navigate(staffPath('/login'), { replace: true });
  };
  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[15rem_1fr]">
      <aside className="border-b border-line bg-white lg:sticky lg:top-0 lg:flex lg:h-dvh lg:flex-col lg:border-b-0 lg:border-r">
        <div className="flex items-center gap-3 px-4 py-4 lg:px-5 lg:py-6">
          <RestaurantAvatar r={restaurant} className="size-10" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{restaurant.name}</p>
            <p className="truncate text-xs text-ink-3">{me.role.name}</p>
          </div>
          <Button variant="ghost" size="sm" className="lg:hidden" onClick={logout} aria-label="Sair" icon={<LogOut className="size-4" />} />
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-1 lg:flex-col lg:overflow-visible lg:pb-0">
          {items.map(({ to, label, icon: Icon, end, phase }) => (
            <NavLink
              key={to}
              to={staffPath(to)}
              end={end}
              className={({ isActive }) =>
                cx(
                  'flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors',
                  isActive ? 'bg-paper-2 text-ink' : 'text-ink-2 hover:bg-paper-2 hover:text-ink',
                )
              }
            >
              <Icon className="size-4" aria-hidden />
              <span className="flex-1">{label}</span>
              {phase && <span className="hidden text-[10px] font-semibold uppercase tracking-wide text-ink-3 lg:inline">Em breve</span>}
            </NavLink>
          ))}
        </nav>
        <div className="hidden border-t border-line p-4 lg:block">
          <p className={cx('mb-3 inline-flex items-center gap-1.5 text-xs', live ? 'text-sprout-600' : 'text-ink-3')} title={live ? 'Atualizações em tempo real ativas' : 'Reconectando; as telas seguem atualizando a cada poucos segundos'}>
            <span className={cx('size-2 rounded-full', live ? 'animate-pulse bg-sprout-500' : 'bg-line')} /> {live ? 'Ao vivo' : 'Reconectando…'}
          </p>
          <p className="truncate text-sm font-medium">{me.user.name}</p>
          <p className="truncate text-xs text-ink-3">{me.user.email}</p>
          <Button variant="secondary" size="sm" className="mt-3 w-full" onClick={logout} icon={<LogOut className="size-4" />}>Sair</Button>
        </div>
      </aside>
      <main className="min-w-0">
        <div className="sticky top-0 z-30">
          <ProductionAlert health={health.data} canManagePrinting={can('printing.manage')} canOperate={can('production.kitchen') || can('production.bar')} />
        </div>
        {/* A tela da cozinha/bar usa a largura toda (tablets e TVs). */}
        <div className={cx('mx-auto px-4 py-6 sm:px-8 sm:py-8', location.pathname.startsWith(staffPath('/producao/')) ? 'max-w-none' : 'max-w-5xl')}>
          <Routes>
            <Route index element={can('dashboard.view') ? <StaffDashboard restaurant={restaurant} name={me.user.name} /> : <StaffHome restaurant={restaurant} me={me} />} />
            {(can('production.kitchen') || can('production.bar')) && <Route path="producao" element={<StationPicker />} />}
            {(can('production.kitchen') || can('production.bar')) && <Route path="producao/:stationId" element={<StationScreen permissions={me.permissions} />} />}
            {can('staff.view') && <Route path="equipe" element={<StaffTeam permissions={me.permissions} />} />}
            {can('printing.manage') && <Route path="impressao" element={<StaffPrinting restaurant={restaurant} permissions={me.permissions} />} />}
            {can('accounts.view') && <Route path="contas" element={<StaffAccounts restaurant={restaurant} permissions={me.permissions} />} />}
            {can('exit.validate') && <Route path="saida" element={<StaffExitDesk restaurant={restaurant} />} />}
            {(can('reports.financial.view') || can('audit.view')) && <Route path="relatorios" element={<StaffReports restaurant={restaurant} />} />}
            {can('payments.integration.manage') && <Route path="pagamentos" element={<StaffPaymentsIntegration restaurant={restaurant} />} />}
            {can('audit.view') && <Route path="auditoria" element={<StaffAudit restaurant={restaurant} canExport={can('reports.export')} />} />}
            {can('orders.view') && <Route path="pedidos" element={<StaffOrders restaurant={restaurant} permissions={me.permissions} />} />}
            {can('tables.view') && <Route path="mesas" element={<StaffTables restaurant={restaurant} permissions={me.permissions} />} />}
            {can('orders.create') && <Route path="mesas/:tableId/pedido" element={<StaffOrderEntry />} />}
            {can('menu.view') && <Route path="cardapio" element={<StaffMenu permissions={me.permissions} />} />}
            {can('restaurant.settings.view') && <Route path="configuracoes" element={<StaffSettings permissions={me.permissions} />} />}
            {items.filter((n) => n.phase).map((n) => (
              <Route key={n.to} path={n.to.replace(/^\//, '')} element={<ComingSoon title={n.label} phase={n.phase!} />} />
            ))}
            <Route path="*" element={<Navigate to={staffPath()} replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function StaffHome({ restaurant, me }: { restaurant: TenantRestaurant; me: Me }) {
  const first = me.user.name.split(' ')[0];
  return (
    <>
      <PageHeader title={`Olá, ${first}`} description={`Você está em ${restaurant.name} como ${me.role.name}.`} />
      {restaurant.status === 'ONBOARDING' && (
        <div className="mb-6">
          <Alert tone="warning" title="Restaurante em implantação">O cardápio público ainda mostra “em breve”. O Garlic ativa o estabelecimento quando a configuração estiver pronta.</Alert>
        </div>
      )}
      <Card>
        <CardHeader title="Suas permissões" description="Definidas pela sua função. Só o administrador do restaurante pode alterá-las." />
        <ul className="grid gap-x-6 gap-y-2 p-5 text-sm sm:grid-cols-2">
          {me.permissions.map((p) => (
            <li key={p} className="flex items-start gap-2">
              <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-sprout-500" />
              {PERMISSIONS[p]}
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

function ComingSoon({ title, phase }: { title: string; phase: number }) {
  return (
    <>
      <PageHeader title={title} />
      <Card>
        <EmptyState icon={<ClipboardList className="size-5" />} title="Módulo em desenvolvimento">
          Este módulo chega na Fase {phase} do Garlic. Seus dados e permissões já estão prontos para ele.
        </EmptyState>
      </Card>
    </>
  );
}

type Log = { id: string; action: string; actorType: string; actorLabel: string | null; createdAt: string };

const AUDIT_FILTERS: [string, string][] = [
  ['', 'Todas as ações'],
  ['payment', 'Pagamentos'],
  ['account', 'Descontos'],
  ['session', 'Comandas'],
  ['order', 'Pedidos e cancelamentos'],
  ['menu', 'Cardápio e preços'],
  ['staff', 'Equipe e acessos'],
  ['role', 'Funções e permissões'],
  ['exit', 'Saída'],
  ['payments.mp', 'Integração Mercado Pago'],
  ['printing', 'Impressão'],
  ['report', 'Exportações'],
  ['platform', 'Acessos do Garlic'],
];

function StaffAudit({ restaurant, canExport }: { restaurant: TenantRestaurant; canExport: boolean }) {
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const qs = new URLSearchParams({ ...(action ? { action } : {}), ...(from ? { from } : {}), ...(to ? { to } : {}) }).toString();
  const q = useQuery({ queryKey: ['staff-audit', qs], queryFn: () => api<Log[]>(`/api/staff/audit?${qs}`) });
  return (
    <>
      <PageHeader title="Auditoria" description="Ações sensíveis neste restaurante, inclusive consultas feitas pela equipe do Garlic. Registros não podem ser alterados nem apagados." />
      <Card className="mb-4 flex flex-wrap items-end gap-2 p-4">
        <select aria-label="Tipo de ação" value={action} onChange={(e) => setAction(e.target.value)} className="h-10 rounded-xl border border-line bg-white px-3 text-sm">
          {AUDIT_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input type="date" aria-label="De" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 rounded-xl border border-line bg-white px-3 text-sm" />
        <input type="date" aria-label="Até" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 rounded-xl border border-line bg-white px-3 text-sm" />
        {canExport && (
          <a href={apiUrl(`/api/staff/audit?${qs}${qs ? '&' : ''}format=csv`)} className="ml-auto inline-flex h-10 items-center gap-2 rounded-xl border border-line bg-white px-3 text-sm font-medium hover:bg-paper-2">
            <FileSpreadsheet className="size-4" /> Exportar CSV
          </a>
        )}
      </Card>
      {q.isPending && <Spinner />}
      {q.isError && <Alert tone="danger">Não foi possível carregar a auditoria.</Alert>}
      {q.data && (
        <Card>
          {q.data.length === 0 ? (
            <EmptyState title="Nenhum evento registrado" />
          ) : (
            <ul className="divide-y divide-line">
              {q.data.map((l) => (
                <li key={l.id} className="flex flex-wrap items-baseline justify-between gap-2 px-5 py-3 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium">
                      {AUDIT_LABEL[l.action] ?? l.action}
                      {l.actorType === 'PLATFORM_ADMIN' && <span className="ml-2 align-middle"><Badge tone="brand">Garlic</Badge></span>}
                    </p>
                    <p className="text-xs text-ink-3">{l.actorLabel ?? 'sistema'}</p>
                  </div>
                  <time className="text-xs text-ink-3" dateTime={l.createdAt}>{formatDateTime(l.createdAt, restaurant.timezone)}</time>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </>
  );
}
